import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CategoriesSlider = ({ categories = [], onCategorySelect, selectedCategoryId }) => {
  const scrollRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  if (categories.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      {/* Navigation Arrows */}
      <button
        onClick={scrollLeft}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 hover:bg-white rounded-full shadow-md flex items-center justify-center transition-all duration-200"
        aria-label="Scroll categories left"
      >
        <Icon name="ChevronLeft" size={16} className="text-gray-600" />
      </button>

      <button
        onClick={scrollRight}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 hover:bg-white rounded-full shadow-md flex items-center justify-center transition-all duration-200"
        aria-label="Scroll categories right"
      >
        <Icon name="ChevronRight" size={16} className="text-gray-600" />
      </button>

      {/* Categories Container */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide px-10 py-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {/* All Categories Option */}
        <button
          onClick={() => onCategorySelect('all')}
          className={`flex-shrink-0 flex flex-col items-center gap-2 p-3 rounded-lg transition-all duration-200 group border-2 ${
            String(selectedCategoryId) === 'all'
              ? 'bg-primary/10 ring-2 ring-primary shadow-md border-primary'
              : 'bg-white hover:bg-gray-50 shadow-sm hover:shadow-md border-gray-200 hover:border-primary/50'
          }`}
        >
          <div className={`w-16 h-16 rounded-lg overflow-hidden transition-colors duration-200 flex items-center justify-center ${
            String(selectedCategoryId) === 'all'
              ? 'border-primary bg-primary/5'
              : 'border-gray-200 group-hover:border-primary/50 bg-gray-50'
          }`}>
            <Icon name="Grid3X3" size={24} className={
              String(selectedCategoryId) === 'all'
                ? 'text-primary'
                : 'text-gray-500 group-hover:text-primary'
            } />
          </div>
          <div className="text-center">
            <span className={`text-xs font-medium block leading-tight ${
              String(selectedCategoryId) === 'all'
                ? 'text-primary'
                : 'text-gray-700 group-hover:text-primary'
            }`}>
              All
            </span>
          </div>
        </button>

        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => onCategorySelect(String(category.id))}
            className={`flex-shrink-0 flex flex-col items-center gap-2 p-3 rounded-lg transition-all duration-200 group border-2 ${
              String(selectedCategoryId) === String(category.id)
                ? 'bg-primary/10 ring-2 ring-primary shadow-md border-primary'
                : 'bg-white hover:bg-gray-50 shadow-sm hover:shadow-md border-gray-200 hover:border-primary/50'
            }`}
          >
            {/* Category Image */}
            <div className={`w-16 h-16 rounded-lg overflow-hidden transition-colors duration-200 ${
              String(selectedCategoryId) === String(category.id)
                ? 'border-primary'
                : 'border-gray-200 group-hover:border-primary/50'
            }`}>
              <Image
                src={category.image}
                alt={category.category}
                className="w-16 h-16 object-cover rounded-lg"
                fallback={
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-lg">
                    <Icon name="ImageOff" size={20} className="text-gray-400" />
                  </div>
                }
              />
            </div>

            {/* Category Name */}
            <div className="text-center">
              <span className={`text-xs font-medium block leading-tight ${
                String(selectedCategoryId) === String(category.id)
                  ? 'text-primary'
                  : 'text-gray-700 group-hover:text-primary'
              }`}>
                {category.category}
              </span>
              {category.properties_count && (
                <span className="text-xs text-gray-500">
                  {/* ({category.properties_count}) */}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Gradient Overlays for Better UX */}
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
    </div>
  );
};

export default CategoriesSlider;